import clsx from "clsx";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdAccountBox,
  MdAttachMoney,
  MdCached,
  MdCategory,
  MdOutlineCreditCard,
  MdPalette,
  MdVoiceChat,
  MdOutlineStorefront,
  MdHelpCenter,
  MdConnectWithoutContact,
} from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import { ICON_SIZES } from "src/helpers/constants";
import useMobile from "src/hooks/useMobile";
import { motion } from "framer-motion";
import NotifyDot from "../NotifyDot/NotifyDot";

interface ISettingsBlocks {
  layout?: "horizontal" | "vertical";
  columns?: number;
  hideTitle?: boolean;
}

const SettingsBlocks: React.FC<ISettingsBlocks> = ({
  layout = "horizontal",
  columns = 1,
  hideTitle = false,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();

  const location = useLocation();

  const iconSize = useMemo(() => {
    if (layout === "vertical") {
      return isMobile ? ICON_SIZES.MD : ICON_SIZES.LG;
    } else {
      return isMobile ? ICON_SIZES.LG * 1.2 : ICON_SIZES.XL * 1.2;
    }
  }, [layout, isMobile]);

  const BLOCKS = [
    {
      text: t("settings.profile_title"),
      description: t("settings.profile_description"),
      icon: (
        <MdAccountBox
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/profile",
              "text-green-500": location.pathname !== "/settings/profile",
            },
          ])}
        />
      ),
      link: "/settings/profile",
      hide: location.pathname === "/settings/profile",
      active: location.pathname === "/settings/profile",
    },
    {
      text: "Contactos",
      description: "Gestiona tu red de contactos",
      icon: (
        <MdConnectWithoutContact
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/contacts",
              "text-fuchsia-500": location.pathname !== "/contacts",
            },
          ])}
        />
      ),
      link: "/contacts",
      active: location.pathname === "/contacts",
    },
    {
      text: t("settings.accounts_title"),
      description: t("settings.accounts_description"),
      icon: (
        <MdOutlineCreditCard
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/accounts",
              "text-blue-500": location.pathname !== "/settings/accounts",
            },
          ])}
        />
      ),
      link: "/settings/accounts",
      active: location.pathname.startsWith("/settings/accounts"),
    },

    {
      text: t("settings.categories_title"),
      description: t("settings.categories_description"),
      icon: (
        <MdCategory
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/categories",
              "text-yellow-500": location.pathname !== "/settings/categories",
            },
          ])}
        />
      ),
      link: "/settings/categories",
      active: location.pathname.startsWith("/settings/categories"),
    },
    {
      text: t("Orígenes y Destinos"),
      description: t("Configura tus fuentes de ingresos y gastos"),
      icon: (
        <MdOutlineStorefront
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/sources",
              "text-teal-500": location.pathname !== "/settings/sources",
            },
          ])}
        />
      ),
      link: "/settings/sources",
      active: location.pathname.startsWith("/settings/sources"),
    },
    {
      text: t("settings.payment_methods_title"),
      description: t("settings.payment_methods_description"),
      icon: (
        <MdAttachMoney
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/payment-methods",
              "text-orange-500":
                location.pathname !== "/settings/payment-methods",
            },
          ])}
        />
      ),
      link: "/settings/payment-methods",
      active: location.pathname === "/settings/payment-methods",
    },
    {
      text: t("Asistentes de voz"),
      description: t("Configura el acceso para tus asistente de voz"),
      icon: (
        <MdVoiceChat
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500":
                location.pathname === "/settings/voice-assistants",
              "text-cyan-400":
                location.pathname !== "/settings/voice-assistants",
            },
          ])}
        />
      ),
      link: "/settings/voice-assistants",
      hide: location.pathname === "/settings/voice-assistants",
      active: location.pathname === "/settings/voice-assistants",
    },
    {
      text: "Apariencia",
      description: "Personaliza la interfaz",
      icon: (
        <MdPalette
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/colors",
              "text-pink-500": location.pathname !== "/settings/colors",
            },
          ])}
        />
      ),
      link: "/settings/colors",
      active: location.pathname === "/settings/colors",
    },
    {
      text: t("Borrar caché"),
      description: t("Borra la caché de la aplicación"),
      icon: (
        <MdCached
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/cache",
              "text-red-400": location.pathname !== "/settings/cache",
            },
          ])}
        />
      ),
      link: "/settings/cache",
      hide: location.pathname === "/settings/cache",
      active: location.pathname === "/settings/cache",
    },
    {
      text: t("Centro de ayuda"),
      description: t("¿Necesitas ayuda?"),
      icon: (
        <MdHelpCenter
          size={iconSize}
          className={clsx([
            "group-hover:text-red-500",
            {
              "text-red-500": location.pathname === "/settings/help",
              "text-orange-400": location.pathname !== "/settings/help",
            },
          ])}
        />
      ),
      link: "/settings/help",
      hide: location.pathname === "/settings/help",
      active: location.pathname === "/settings/help",
    },
  ];

  return (
    <div className={clsx(["flex", "w-full", "flex-col", "gap-4"])}>
      <div
        className={clsx([
          "grid",
          {
            "grid-cols-1 md:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-5":
              layout === "horizontal",
            [`grid-cols-${columns}`]: layout === "vertical",
          },
          "gap-2 md:gap-2",
        ])}
      >
        {BLOCKS.map?.((block: any, key) => {
          return (
            <motion.div
              initial={{
                opacity: 0,
                x: key % 2 === 0 ? -200 : 200,
                scale: 1,
              }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.6,
                ease: "easeOut",
                type: "spring",
                stiffness: 400,
                damping: 17,
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.9 }}
              key={key}
              className={clsx(["flex", "w-full"])}
            >
              <Link
                className={clsx([
                  "flex",
                  "w-full",
                  {
                    "flex-row md:flex-col bg-sky-100": layout === "horizontal",
                    "flex-col md:flex-row bg-sky-100 md:bg-white":
                      layout === "vertical",
                    "md:py-6": layout === "horizontal",
                  },
                  "p-2 md:p-4",
                  "gap-1 md:gap-3",
                  "rounded-md md:rounded-md",
                  "dark:bg-gray-900/50",
                  "group",
                ])}
                to={block?.link || ""}
              >
                <div
                  className={clsx([
                    "flex",
                    "flex-row",
                    "items-center",
                    "relative",
                  ])}
                >
                  {block?.active && <NotifyDot />}
                  {block.icon}
                </div>
                <div className={clsx(["flex", "flex-col", "justify-center"])}>
                  <div
                    className={clsx("text-sm", "md:text-base", "font-medium")}
                  >
                    {block.text}
                  </div>
                  <div className={clsx(["text-sm", "md:text-sm"])}>
                    {block.description}
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default SettingsBlocks;
